import { defineStore } from 'pinia'
import { ref } from 'vue'

export type MessageType = 'info' | 'success' | 'warning' | 'error'

const DEDUP_WINDOW_MS = 1500

const AUTO_DISMISS_MS: Record<MessageType, number> = {
  info: 4000,
  success: 3000,
  warning: 5000,
  error: 6000,
}

interface QueuedToast {
  msg: string
  msgType: MessageType
  durationMs?: number
}

export const useInfoStore = defineStore('info', () => {
  const show = ref(false)
  const message = ref('')
  const type = ref<MessageType>('info')
  let dismissTimer: ReturnType<typeof setTimeout> | null = null

  const queue: QueuedToast[] = []
  let lastKey = ''
  let lastAt = 0

  function cancelDismissTimer() {
    if (dismissTimer !== null) {
      clearTimeout(dismissTimer)
      dismissTimer = null
    }
  }

  function showNext() {
    if (queue.length === 0) {
      show.value = false
      message.value = ''
      return
    }
    const next = queue.shift()!
    message.value = next.msg
    type.value = next.msgType
    show.value = true

    cancelDismissTimer()
    dismissTimer = setTimeout(() => {
      dismissTimer = null
      showNext()
    }, next.durationMs ?? AUTO_DISMISS_MS[next.msgType])
  }

  function trigger(msg: string, msgType: MessageType = 'info', durationMs?: number): void {
    const now = Date.now()
    const key = `${msgType}::${msg}`
    if (key === lastKey && now - lastAt < DEDUP_WINDOW_MS) return
    lastKey = key
    lastAt = now

    queue.push({ msg, msgType, durationMs })
    if (!show.value) {
      showNext()
    }
  }

  function clear() {
    cancelDismissTimer()
    queue.length = 0
    show.value = false
    message.value = ''
    lastKey = ''
    lastAt = 0
  }

  return { show, message, type, trigger, clear }
})
